import { useCallback, useState } from 'react';
import dayjs from 'dayjs';
import { sessionsMatch } from '@discover-lib/utils';
import { type RestaurantDetails } from '@discover-data/stores/restaurant/gql/getters/restaurantDetails';
import { type RegularAvailabilitySlotType } from '@discover-data/stores/restaurant/model/types';
import { useStoreActions } from '@discover-data/stores/restaurant/hooks';
import getAvailabilitySlotStatus from '@discover-data/stores/restaurant/utils/slots/getAvailabilitySlotStatus';
import isSlotAvailable from '@discover-data/stores/restaurant/utils/slots/isSlotAvailable';
import useDate from '@discover-data/stores/hooks/date/useDate';
import usePeople from '@discover-data/stores/hooks/people/usePeople';
import useSession from '@discover-data/stores/hooks/session/useSession';
import AvailabilitySlot from '@discover-components/availability/slot';
import { useAvailabilityOptionsDialogContext } from '@discover-components/availability/options/dialog';
import AvailabilityCarouselItemSkeleton from '@discover-components/availability/carousel/item/skeleton';
import Dots from '@discover-ui/dots';
import { WithRegularBooking } from '@/components/hocs/withRegularBooking';
import hasNonEmptyAvailabilityOptions from './hasNonEmptyAvailabilityOptions';
import getSlotLabel from './getSlotLabel';

export type RegularAvailabilitySlotProps = {
  restaurant: NonNull<Pick<RestaurantDetails, 'id'>>;
  slot: RegularAvailabilitySlotType & { hasMultipleTimes: boolean };
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
} & Pick<WithRegularBooking, 'handleBooking' | 'isProcessing' | 'selectedTime'>;

export default function RegularAvailabilitySlot({
  restaurant,
  slot,
  selectedDate,
  setSelectedDate,
  handleBooking,
  isProcessing,
  selectedTime,
}: RegularAvailabilitySlotProps) {
  const [date] = useDate();
  const [people] = usePeople();
  const [session] = useSession();
  const fetchSearchAvailability = useStoreActions((actions) => actions.allAvailabilitySearch.fetch);
  const { selectedSlot, selectedRestaurantId, setSelected } = useAvailabilityOptionsDialogContext();
  const [loading, setLoading] = useState(false);

  const onSelect = useCallback(async () => {
    let { hasMultipleTimes } = slot;

    // get the slot data if we don't have it already
    if (slot.dataStatus !== 'CACHED') {
      setLoading(true);

      const searchAvailability = await fetchSearchAvailability({
        restaurantId: restaurant.id,
        date: slot.date,
        people,
      });

      const availableSlots = (searchAvailability?.availableTimes || []).filter(
        (searchSlot) =>
          searchSlot && sessionsMatch(searchSlot.session, session) && isSlotAvailable(searchSlot)
      );

      setLoading(false);

      if (availableSlots.length === 0) {
        return;
      }

      hasMultipleTimes = availableSlots.length > 1;
    } else if (dayjs.utc(slot.syncExpires).isBefore(dayjs(), 'minute')) {
      // Trigger update if the data is stale
      fetchSearchAvailability({
        restaurantId: restaurant.id,
        date: slot.date,
        people,
      });
    }

    if (hasMultipleTimes && !selectedDate && date === 'week') {
      setSelectedDate(slot.date);
    } else if (hasNonEmptyAvailabilityOptions(slot) && setSelected) {
      setSelected({ selectedSlot: slot, selectedRestaurantId: restaurant.id });
    } else {
      if (!isSlotAvailable(slot)) {
        return;
      }

      handleBooking({ time: slot.time, timeSlot: slot, guests: people });
    }
  }, [
    date,
    fetchSearchAvailability,
    handleBooking,
    people,
    restaurant.id,
    selectedDate,
    session,
    setSelected,
    setSelectedDate,
    slot,
  ]);

  if (loading) {
    return (
      <AvailabilityCarouselItemSkeleton className="flex size-full flex-col items-center justify-center lg:size-full">
        <Dots className="text-gray" />
      </AvailabilityCarouselItemSkeleton>
    );
  }

  const status = getAvailabilitySlotStatus(slot);

  return (
    <AvailabilitySlot
      date={slot.date}
      time={slot.time}
      cutOffTime={slot.cutOffTime}
      status={isProcessing && slot.time === selectedTime?.time ? 'processing' : status}
      hasMultipleTimes={slot.hasMultipleTimes}
      availableLabel={getSlotLabel(status, date, selectedDate, slot.time)}
      badge={slot.deal ?? 'Regular'}
      badgeClassName={slot.deal ? 'bg-teal' : 'bg-blue-400'}
      selected={
        !!selectedSlot &&
        selectedSlot.date === slot.date &&
        selectedSlot.time === slot.time &&
        !!selectedRestaurantId &&
        selectedRestaurantId === restaurant.id
      }
      onSelect={onSelect}
    />
  );
}
